import { ReactNode } from 'react';
import { GroupBase, MultiValue, MultiValueGenericProps, components } from 'react-select';
import { Add16 } from 'ui/icons/action/Add16';
import { DefaultCreateLabel } from './DefaultCreateLabel';
import {
  EllipsizedItem,
  SelectCompactItem,
  SelectCompactItemCount,
  SelectGroupDivider,
  SelectGroupHeader,
  SelectGroupWrapper,
} from './Select.styles';
import {
  BaseOption,
  CreatableSelectProps,
  CreateOptionPosition,
  OnCreateHandler,
  OnSelectHandler,
} from './types';

/**
 * createGroup: function used to create grouping of options.
 * it will automatically add a "select all"/"deselect all" option if
 * the select is a multi select and no optionLimit is provided
 */
export const createGroup = (
  groupName: string | number,
  options: BaseOption[],
  value: BaseOption[],
  category: string,
  optionLimit?: number,
  isMulti?: boolean,
  showCategoryDivider?: boolean,
  index?: number,
): GroupBase<BaseOption> => {
  const valueArr = Object.values(value).map((val: BaseOption) => val.value);
  const groupValueArr = Object.values(options).map((val: BaseOption) => val.value);
  const allOptionsSelected = groupValueArr.every((v) => valueArr.includes(v));
  const selectText = allOptionsSelected ? 'Deselect all' : 'Select all';

  let opts = options;
  if (!optionLimit && isMulti) {
    opts = [
      {
        label: selectText,
        value: `$select-all-${category}`,
        selectAllCategory: category,
        allCategoryOptionsSelected: allOptionsSelected,
      },
      ...options,
    ];
  }

  return {
    /**
     * WARNING: `label` is expected to be a string per react-select types, but we're using a ReactNode here.
     * Leaving it as a ReactNode for now to avoid breaking changes, but we should follow up here or open an issue to update the type.
     */
    label: (
      <SelectGroupWrapper
        data-category-index={index}
        data-show-category-divider={!!showCategoryDivider}
      >
        {showCategoryDivider && <SelectGroupDivider />}
        {groupName && <SelectGroupHeader>{groupName}</SelectGroupHeader>}
      </SelectGroupWrapper>
    ) as unknown as string,
    options: opts,
  };
};

/**
 * groupOptions: function used to group options by category.
 * it's a sub-function of "createGroup" and broken out for easier testing
 */
export const groupOptions = (
  options: BaseOption[],
  categories: Record<string, string | number>,
  value: BaseOption[],
  optionLimit?: number,
  isMulti?: boolean,
  showCategoryDivider?: boolean,
) => {
  const groupArr: GroupBase<BaseOption>[] = [];
  Object.keys(categories).forEach((category, index) => {
    const currentOptions: BaseOption[] = [];
    options.forEach((option) => {
      if (option.categoryId === category) {
        currentOptions.push(option);
      }
    });

    const shouldShowCategoryDivider = index !== 0 && showCategoryDivider;

    groupArr.push(
      createGroup(
        categories[category],
        currentOptions,
        value,
        category,
        optionLimit,
        isMulti,
        shouldShowCategoryDivider,
        index,
      ),
    );
  });

  return groupArr.filter((group) => group.options.length > 0); // filter out groups with no options
};

export const getCompactMultiValueContainer =
  (compactLimit?: number) => (props: MultiValueGenericProps<BaseOption<string>>) => {
    const { selectProps, data } = props;
    const { isDisabled, menuIsOpen } = selectProps;

    if (!compactLimit) {
      return (
        <EllipsizedItem data-test={`multi-select-value-${data.value}`} title={data.label}>
          <components.MultiValueContainer {...props} />
        </EllipsizedItem>
      );
    }

    const { label } = data;
    const allSelected = selectProps.value as MultiValue<BaseOption<string>>;
    const index = allSelected.findIndex((selected) => selected.label === label);
    const isLastSelected = index === allSelected.length - 1;

    let val: ReactNode = null;

    if (allSelected.length > compactLimit && isLastSelected) {
      // add disabled styling here
      val = (
        <SelectCompactItemCount data-disabled={isDisabled}>
          <Add16 height={10} width={10} isColorInherited />
          {allSelected.length - compactLimit}
        </SelectCompactItemCount>
      );
    } else if (index < compactLimit - 1 && !isLastSelected) {
      // and here
      val = (
        <SelectCompactItem
          data-test={`multi-select-compact-value-${data.value}`}
          data-disabled={isDisabled}
        >
          {label},
        </SelectCompactItem>
      );
    } else if (index === compactLimit - 1 || (index < compactLimit - 1 && isLastSelected)) {
      val = (
        <SelectCompactItem
          data-test={`multi-select-compact-value-${data.value}`}
          data-disabled={isDisabled}
        >
          {label}
        </SelectCompactItem>
      );
    }

    return !menuIsOpen ? (
      val
    ) : (
      <EllipsizedItem data-test={`multi-select-value-${data.value}`} title={data.label}>
        <components.MultiValueContainer {...props} />
      </EllipsizedItem>
    );
  };

export const parseSelectAlls = (
  selectedOptions: BaseOption[],
  setValue: any,
  allOptions: any[],
) => {
  const categoriesToDeselectAll = new Set();
  const categoriesToSelectAll = new Set();

  const filteredSelectedOptions = selectedOptions.reduce((acc: BaseOption[], val: BaseOption) => {
    if (val.selectAllCategory) {
      if (val.allCategoryOptionsSelected) {
        categoriesToDeselectAll.add(val.selectAllCategory);
      } else {
        categoriesToSelectAll.add(val.selectAllCategory);
      }
      return acc;
    }
    return [...acc, val];
  }, []);
  const parsedBySelectAlls: BaseOption[] = [];
  const currentValueSet = new Set();
  filteredSelectedOptions.forEach((opt) => {
    if (!categoriesToDeselectAll.has(opt.categoryId)) {
      parsedBySelectAlls.push(opt);
      currentValueSet.add(opt.value);
    }
  });

  const allOptionValues = allOptions.reduce((acc, val) => {
    return [...acc, ...val.options];
  }, []);

  const optionsToAddFromSelectAll = allOptionValues.reduce((acc: BaseOption[], val: BaseOption) => {
    if (categoriesToSelectAll.has(val.categoryId) && !currentValueSet.has(val.value)) {
      return [...acc, val];
    }
    return acc;
  }, []);

  setValue([...parsedBySelectAlls, ...optionsToAddFromSelectAll]);
};

export const addCreatableProps = (
  canCreate = false,
  createPrefix: string,
  createSuffix: string,
  isMulti = false,
  onSelect: OnSelectHandler,
  onCreateOption?: OnCreateHandler,
): CreatableSelectProps =>
  canCreate
    ? {
        formatCreateLabel: (inputValue: string) => (
          <DefaultCreateLabel
            inputValue={inputValue}
            createPrefix={createPrefix}
            createSuffix={createSuffix}
          />
        ),
        createOptionPosition: CreateOptionPosition.first,
        ...(!isMulti && {
          onCreateOption: (inputValue: string) => {
            // when provided, this will be called with the input value when a new option is created,
            // and the default onChange will not be called

            // this logic supports only a single option creation for now

            onCreateOption?.(inputValue);

            const option = { value: inputValue, label: inputValue };
            const selectedOption = Array.isArray(option) ? option : [option];
            onSelect(selectedOption);
          },
        }),
      }
    : {};
